import { useState } from 'react'
import { 
  Ship, 
  Plus, 
  Settings, 
  Zap,
  Shield,
  Target,
  MapPin,
  Clock,
  Users,
  Search,
  Filter
} from 'lucide-react'
import { Button } from '../components/ui/button'

interface Fleet {
  id: string
  name: string
  ships: number
  location: string
  status: 'idle' | 'traveling' | 'combat' | 'exploring'
  destination?: string
  eta?: string
  commander: string
}

const FleetManagement = () => {
  const [fleets] = useState<Fleet[]>([
    {
      id: '1',
      name: 'Alpha Squadron',
      ships: 12,
      location: 'Sol System',
      status: 'idle',
      commander: 'Admiral Drake'
    },
    {
      id: '2',
      name: 'Beta Fleet',
      ships: 8,
      location: 'Kepler-442',
      status: 'traveling',
      destination: 'Proxima Centauri',
      eta: '2h 15m',
      commander: 'Captain Nova'
    },
    {
      id: '3',
      name: 'Gamma Wing',
      ships: 15,
      location: 'Wolf 359',
      status: 'exploring',
      commander: 'Commander Vex'
    },
    {
      id: '4',
      name: 'Delta Force',
      ships: 6,
      location: 'Arcturus',
      status: 'combat',
      commander: 'General Storm'
    }
  ])

  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'idle': return 'text-gray-400 bg-gray-600/20'
      case 'traveling': return 'text-blue-400 bg-blue-600/20'
      case 'combat': return 'text-red-400 bg-red-600/20'
      case 'exploring': return 'text-green-400 bg-green-600/20'
      default: return 'text-gray-400 bg-gray-600/20'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'idle': return Clock
      case 'traveling': return MapPin
      case 'combat': return Zap
      case 'exploring': return Target
      default: return Clock
    }
  }

  const filteredFleets = fleets.filter(fleet => {
    const matchesSearch = fleet.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         fleet.commander.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         fleet.location.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesStatus = statusFilter === 'all' || fleet.status === statusFilter
    return matchesSearch && matchesStatus
  })

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-purple-900 to-slate-900 pt-16">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-white mb-2 flex items-center">
              <Ship className="w-10 h-10 mr-3 text-blue-400" />
              Fleet Command
            </h1>
            <p className="text-gray-400">Manage your space fleets and military operations</p>
          </div>
          <Button className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700">
            <Plus className="w-4 h-4 mr-2" />
            New Fleet
          </Button>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg p-4 border border-white/10">
            <div className="flex items-center justify-between mb-2">
              <Ship className="w-6 h-6 text-blue-400" />
              <span className="text-xs text-gray-400">TOTAL FLEETS</span>
            </div>
            <div className="text-2xl font-bold text-white">{fleets.length}</div>
          </div>
          
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg p-4 border border-white/10">
            <div className="flex items-center justify-between mb-2">
              <Users className="w-6 h-6 text-green-400" />
              <span className="text-xs text-gray-400">TOTAL SHIPS</span>
            </div>
            <div className="text-2xl font-bold text-white">
              {fleets.reduce((total, fleet) => total + fleet.ships, 0)}
            </div>
          </div>
          
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg p-4 border border-white/10">
            <div className="flex items-center justify-between mb-2">
              <Zap className="w-6 h-6 text-red-400" />
              <span className="text-xs text-gray-400">IN COMBAT</span>
            </div>
            <div className="text-2xl font-bold text-white">
              {fleets.filter(f => f.status === 'combat').length}
            </div>
          </div>
          
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg p-4 border border-white/10">
            <div className="flex items-center justify-between mb-2">
              <Target className="w-6 h-6 text-purple-400" />
              <span className="text-xs text-gray-400">EXPLORING</span>
            </div>
            <div className="text-2xl font-bold text-white">
              {fleets.filter(f => f.status === 'exploring').length}
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search fleets, commanders, or locations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-slate-800/50 border border-gray-600 rounded-lg pl-10 pr-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-slate-800/50 border border-gray-600 rounded-lg pl-10 pr-8 py-3 text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent appearance-none min-w-[150px]"
            >
              <option value="all">All Status</option>
              <option value="idle">Idle</option>
              <option value="traveling">Traveling</option>
              <option value="combat">Combat</option>
              <option value="exploring">Exploring</option>
            </select>
          </div>
        </div>

        {/* Fleet List */}
        <div className="space-y-4">
          {filteredFleets.map((fleet) => {
            const StatusIcon = getStatusIcon(fleet.status)
            return (
              <div key={fleet.id} className="bg-slate-800/50 backdrop-blur-sm rounded-lg border border-white/10 overflow-hidden">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center">
                        <Ship className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white">{fleet.name}</h3>
                        <p className="text-gray-400">{fleet.commander}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-4">
                      <div className={`flex items-center space-x-2 px-3 py-1 rounded-full text-sm ${getStatusColor(fleet.status)}`}>
                        <StatusIcon className="w-4 h-4" />
                        <span className="capitalize">{fleet.status}</span>
                      </div>
                      <Button variant="ghost" size="icon">
                        <Settings className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="flex items-center space-x-3">
                      <Users className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-sm text-gray-400">Ships</p>
                        <p className="text-lg font-semibold text-white">{fleet.ships}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-3">
                      <MapPin className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-sm text-gray-400">Location</p>
                        <p className="text-lg font-semibold text-white">{fleet.location}</p>
                      </div>
                    </div>
                    
                    {fleet.destination && (
                      <div className="flex items-center space-x-3">
                        <Target className="w-5 h-5 text-gray-400" />
                        <div>
                          <p className="text-sm text-gray-400">Destination</p>
                          <p className="text-lg font-semibold text-white">{fleet.destination}</p>
                          {fleet.eta && (
                            <p className="text-sm text-blue-400">ETA: {fleet.eta}</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex flex-wrap gap-2 mt-6">
                    <Button variant="outline" size="sm">
                      <Target className="w-4 h-4 mr-2" />
                      Deploy
                    </Button>
                    <Button variant="outline" size="sm">
                      <MapPin className="w-4 h-4 mr-2" />
                      Move
                    </Button>
                    <Button variant="outline" size="sm">
                      <Shield className="w-4 h-4 mr-2" />
                      Fortify
                    </Button>
                    <Button variant="outline" size="sm">
                      <Settings className="w-4 h-4 mr-2" />
                      Configure
                    </Button>
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        {filteredFleets.length === 0 && (
          <div className="text-center py-12">
            <Ship className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-400 mb-2">No fleets found</h3>
            <p className="text-gray-500 mb-6">Try adjusting your filters or create a new fleet</p>
            <Button className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700">
              <Plus className="w-4 h-4 mr-2" />
              Create New Fleet
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}

export default FleetManagement